import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react'
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useClientContextStore, type ClientSummary } from '@/store/clientContext'
import { switchClient } from '@/lib/services/clientContextService'
import { apiClient } from '@/lib/api/client'
import {
  ASSET_CATALOG_NAV,
  assetCatalogBasePath,
  assetCatalogPath,
} from '@/components/assetCatalog/assetCatalogNav'

interface AssetCatalogShellProps {
  children: ReactNode
  /** When true, content area fills remaining height (for DataTable pages) */
  fillHeight?: boolean
}

async function fetchClientById(id: string): Promise<ClientSummary | null> {
  try {
    const { data } = await apiClient.get(`/organization/clients/${id}`)
    const row = data?.data ?? data
    if (!row?.id) return null
    return {
      id: Number(row.id),
      code: String(row.code ?? ''),
      name: String(row.name ?? row.code ?? id),
      database_name: row.database_name ?? undefined,
      status: row.status,
    }
  } catch {
    return null
  }
}

/**
 * Two-column Asset Catalog workspace for a single client.
 * Opened from Organization → Clients → Manage Asset Catalog.
 */
export function AssetCatalogShell({ children, fillHeight = true }: AssetCatalogShellProps) {
  const colors = useThemeColors()
  const { pathname } = useLocation()
  const { clientId } = useParams<{ clientId: string }>()
  const activeClient = useClientContextStore((s) => s.activeClient)
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)

  const [hydrating, setHydrating] = useState(true)
  const [hydrateError, setHydrateError] = useState<string | null>(null)

  const activeBg = useColorModeValue('blue.50', 'whiteAlpha.200')
  const activeColor = useColorModeValue('blue.700', 'blue.200')
  const idleColor = useColorModeValue('gray.700', 'gray.300')
  const railBg = useColorModeValue('gray.50', 'gray.900')

  const relativePath = useMemo(() => {
    if (!clientId) return ''
    const base = assetCatalogBasePath(clientId)
    if (!pathname.startsWith(base)) return ''
    return pathname.slice(base.length).replace(/^\/+/, '')
  }, [pathname, clientId])

  useEffect(() => {
    let cancelled = false

    async function hydrate() {
      if (!clientId) {
        setHydrating(false)
        setHydrateError('Missing client.')
        return
      }

      setHydrating(true)
      setHydrateError(null)

      // Already bound to this client
      if (activeClient && String(activeClient.id) === String(clientId) && activeClientCode) {
        setHydrating(false)
        return
      }

      const summary = await fetchClientById(clientId)
      if (cancelled) return

      if (!summary?.code) {
        setHydrateError('Client not found or has no code.')
        setHydrating(false)
        return
      }

      try {
        const switched = await switchClient(summary.code)
        if (cancelled) return
        setActiveClient(switched ?? summary)
      } catch {
        if (cancelled) return
        setActiveClient(summary)
      } finally {
        if (!cancelled) setHydrating(false)
      }
    }

    void hydrate()
    return () => {
      cancelled = true
    }
    // Only re-run when URL client changes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid loop on store updates
  }, [clientId])

  if (!clientId) {
    return (
      <AdminLayout>
        <Box p={6}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            Open Asset Catalog from Organization → Clients using Manage Asset Catalog.
            <Button as={RouterLink} to="/organization/clients" ml={3} size="sm">
              Go to Clients
            </Button>
          </Alert>
        </Box>
      </AdminLayout>
    )
  }

  const clientLabel =
    activeClient && String(activeClient.id) === String(clientId)
      ? `${activeClient.name} (${activeClient.code})`
      : `Client #${clientId}`

  return (
    <AdminLayout>
      <Box h="100%" display="flex" flexDirection="column" overflow="hidden" minH={0}>
        <Box
          flexShrink={0}
          borderBottom="1px"
          borderColor={colors.border}
          bg={colors.cardBg}
          px={4}
          py={3}
        >
          <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="semibold" color={colors.heading}>
                Manage Asset Catalog
              </Text>
              <Text fontSize="sm" color={colors.textSecondary}>
                Reference data for this client&apos;s inventory and ticket assets
              </Text>
            </VStack>
            <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="md">
              {clientLabel}
            </Badge>
          </HStack>
        </Box>

        {hydrating ? (
          <HStack justify="center" py={16} spacing={3}>
            <Spinner />
            <Text color={colors.textSecondary}>Loading client catalog…</Text>
          </HStack>
        ) : hydrateError ? (
          <Box p={6}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {hydrateError}
              <Button as={RouterLink} to="/organization/clients" ml={3} size="sm">
                Back to Clients
              </Button>
            </Alert>
          </Box>
        ) : (
          <HStack
            align="stretch"
            spacing={0}
            flex="1"
            minH={0}
            overflow="hidden"
          >
            <Box
              as="nav"
              w={{ base: '160px', md: '220px' }}
              flexShrink={0}
              borderRight="1px"
              borderColor={colors.border}
              bg={railBg}
              overflowY="auto"
              py={3}
              px={2}
            >
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color={colors.textMuted}
                textTransform="uppercase"
                px={2}
                mb={2}
              >
                Catalog
              </Text>
              <VStack align="stretch" spacing={0.5}>
                {ASSET_CATALOG_NAV.map((item, index) => {
                  const href = assetCatalogPath(clientId, item.segment)
                  const isActive = item.match(relativePath)
                  const prevGroup = index > 0 ? ASSET_CATALOG_NAV[index - 1]?.group : undefined
                  const showGroupLabel =
                    item.group &&
                    item.group !== prevGroup &&
                    (item.group === 'core' || item.group === 'specs' || item.group === 'other')
                  const groupLabel =
                    item.group === 'core'
                      ? 'Core'
                      : item.group === 'specs'
                        ? 'Spec options'
                        : item.group === 'other'
                          ? 'Other'
                          : null

                  return (
                    <Box key={item.segment}>
                      {showGroupLabel && groupLabel && (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={colors.textMuted}
                          textTransform="uppercase"
                          px={2}
                          mt={index === 0 ? 0 : 3}
                          mb={1}
                        >
                          {groupLabel}
                        </Text>
                      )}
                      <Button
                        as={RouterLink}
                        to={href}
                        size="sm"
                        w="100%"
                        justifyContent="flex-start"
                        variant="ghost"
                        fontWeight={isActive ? 'semibold' : 'medium'}
                        color={isActive ? activeColor : idleColor}
                        bg={isActive ? activeBg : 'transparent'}
                        _hover={{ bg: activeBg }}
                      >
                        {item.label}
                      </Button>
                    </Box>
                  )
                })}
              </VStack>
              <Button
                as={RouterLink}
                to="/organization/clients"
                size="xs"
                variant="link"
                mt={4}
                mx={2}
                color={colors.textMuted}
              >
                ← Back to Clients
              </Button>
            </Box>

            <Box
              flex="1"
              minW={0}
              minH={0}
              overflow={fillHeight ? 'hidden' : 'auto'}
              display="flex"
              flexDirection="column"
              p={4}
            >
              {children}
            </Box>
          </HStack>
        )}
      </Box>
    </AdminLayout>
  )
}
